import { ThreeDStockMaterialsListItemButtonsMaterial } from './ThreeDStockMaterialsListItemButtons.types';
import { UpdateMaterialCacheAction } from '../../../../../../materials/materialsTypes';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { DownloadMaterialButton } from '../../../../../../materials/components/buttons/DownloadMaterialButton';
import { DownloadMaterialSeparatelyButton } from '../../../../../../materials/components/buttons/DownloadMaterialSeparatelyButton';
import { MaterialFavoriteButton } from '../../../../../../materials/components/buttons/MaterialFavoriteButton';
import { MaterialsCopyLinkButton } from '../../../../../../materials/components/buttons/MaterialsCopyLinkButton';
import { SubmitMaterialToProjectOrTaskModalButton } from '../../../../../../materials/components/lists/MaterialsListItem/components/SubmitMaterialToProjectOrTaskModalButton';

import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';
import { NextLinkHelper } from '../../../../../../../helpers/links/NextLinkHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';
import { DropdownHelper } from '../../../../../../../helpers/dropdowns/DropdownHelper';

import { MaterialsPermissions } from '../../../../../../materials/materialsConstants';

import { PopoverPlacement } from '../../../../../../../helpers/Popover/popoverConstants';

import { words } from '../../../../../../../locales/keys';

interface ThreeDStockMaterialsListItemButtonsProps {
  material: ThreeDStockMaterialsListItemButtonsMaterial;
  downloadButtonAction: string;
  downloadSeparatelyButtonAction: string;
  materialEditPagePath: string;
  updateMaterialCache?: UpdateMaterialCacheAction<ThreeDStockMaterialsListItemButtonsMaterial>;
  onEditButtonMouseEnter?: () => void;
}

function ThreeDStockMaterialsListItemButtons({
  material,
  downloadButtonAction,
  downloadSeparatelyButtonAction,
  updateMaterialCache,
  onEditButtonMouseEnter,
  materialEditPagePath
}: ThreeDStockMaterialsListItemButtonsProps) {
  return (
    <div className="flex justify-between gap-1">
      <div className="min-w-0">
        <CheckPermissions
          action={MaterialsPermissions.READ_MATERIAL_FAVORITE_BUTTON}
        >
          <MaterialFavoriteButton
            dataGa="materials-material-favorite-button"
            material={material}
            icon={
              material.favorite
                ? IconsEnum.HEART_SOLID
                : IconsEnum.HEART_OUTLINE
            }
            iconClassName="h-5 w-5 shrink-0"
            i18nText={material.favorite ? words.inFavorites : words.toFavorites}
            i18nTextClassName="truncate"
            updateMaterialCache={updateMaterialCache}
            className={
              material.favorite
                ? 'py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-white bg-pink-500 hover:bg-pink-600 shadow-sm hover:shadow-md max-w-full transition-colors duration-150 ease-out'
                : 'py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-white bg-black hover:bg-gray-900 shadow-sm hover:shadow-md max-w-full transition-colors duration-150 ease-out'
            }
          />
        </CheckPermissions>
      </div>

      <div className="flex gap-1">
        <CheckPermissions action={downloadButtonAction}>
          <DownloadMaterialButton
            className="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            dataGa="materials-material-download-button"
            icon={IconsEnum.DOWNLOAD_SOLID}
            iconClassName="h-5 w-5"
            materialUuid={material.uuid}
            tooltipI18nText={words.download}
            tooltipSingleton
          />
        </CheckPermissions>

        <CheckPermissions action={downloadSeparatelyButtonAction}>
          <DownloadMaterialSeparatelyButton
            className="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            dataGa="materials-material-download-separately-button"
            icon={IconsEnum.DOWNLOAD_SOLID}
            iconClassName="h-5 w-5"
            materialUuid={material.uuid}
            tooltipI18nText={words.download}
            tooltipSingleton
          />
        </CheckPermissions>

        <DropdownHelper
          className="relative flex items-center justify-center"
          buttonClassName="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
          icon={IconsEnum.DOTS_HORIZONTAL_OUTLINE}
          popoverClassName="flex flex-col items-end gap-1"
          dropdownPlacement={PopoverPlacement.TOP_END}
        >
          <CheckPermissions
            action={
              MaterialsPermissions.READ_MATERIAL_SUBMIT_TO_TASK_OR_PROJECT_BUTTON
            }
          >
            <SubmitMaterialToProjectOrTaskModalButton
              dataGa="materials-material-submit-to-task-or-project-button"
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              material={material}
              i18nText={words.attach}
              i18nTextClassName="inline"
              iconClassName="w-5 h-5"
            />
          </CheckPermissions>

          <CheckPermissions
            action={MaterialsPermissions.READ_MATERIAL_COPY_LINK_BUTTON}
          >
            <MaterialsCopyLinkButton
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              dataGa="materials-material-copy-link-button"
              icon={IconsEnum.LINK_SOLID}
              iconClassName="h-5 w-5"
              material={material}
              i18nText={words.copyLink}
              i18nTextClassName="inline"
            />
          </CheckPermissions>

          <CheckPermissions
            action={MaterialsPermissions.READ_MATERIAL_EDIT_BUTTON}
          >
            <NextLinkHelper
              onMouseEnter={onEditButtonMouseEnter}
              dataGa="materials-material-edit-link"
              href={materialEditPagePath}
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            >
              <Icon icon={IconsEnum.PENCIL} className="w-5 h-5" />
              <span>
                <Translate id={words.edit} />
              </span>
            </NextLinkHelper>
          </CheckPermissions>
        </DropdownHelper>
      </div>
    </div>
  );
}

export default ThreeDStockMaterialsListItemButtons;
